import React, { Fragment, useState, version } from "react";
import { useSelector } from "react-redux";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useParams, matchPath, useNavigate } from "react-router-dom";

import { Button, Grid } from "@mui/material";

import { Save, Refresh, Article } from "@mui/icons-material";
import CenterFocus from "@mui/icons-material/CenterFocusStrong";

import MoveIcon from "../../../icons/MoveIcon";
import ToggleGridIcon from "../../../icons/ToggleGridIcon";

import apiClient from "../../../../services/api";

import store from "../../../../store";
import {
    setStoreData,
    setUnsavedModifications,
    setIsDragEnabled,
    setIsInfoWindowVisible,
    setIsSceneUpdated,
    setIsGridEnabled,
    setRecenterMap,
    setStoreUpdated,
    setFloorPlanElements,
    setSelectedStoreElement,
    setIsFloorEdit
} from "../../../../store/actions/Store";
import { toggleMessageOpen } from "../../../../store/actions/Message";
import { createConfirmAction } from "../../../../store/actions/ConfirmDialog";
import CustomDialog from '../../../partials/CustomDialog';
import CubeIcon from "../../../icons/CubeIcon";
import CubesIcon from "../../../icons/CubesIcon";
import EditPencilIcon from "../../../icons/EditPencilIcon";
import StyledIconButton from '../../../partials/easyFloor/StyledIconButton';

import { setSideBarIsOpened } from "../../../../store/actions/Store";

export default function MapRightSidebar() {
    const { t } = useTranslation();
    const { floorPlanId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const storeData = useSelector((state) => state.store.storeData);
    const unsavedModifications = useSelector((state) => state.store.unsavedModifications);
    const isGridEnabled = useSelector((state) => state.store.isGridEnabled);
    const isDragEnabled = useSelector((state) => state.store.isDragEnabled);
    const isStoreEdit = useSelector((state) => state.store.isFloorEdit);
    const open = useSelector((state) => state.store.sideBarIsOpened);

    const [isSaveFloorPlanDialogOpen, setIsSaveFloorPlanDialogOpen] = useState(false);

    const is3D = matchPath({ path: '/app/easy-floor/map/' + floorPlanId + '/preview', end: true }, location.pathname);

    const getFloorPlanVersionId = (number) => {
        if(!storeData || !storeData.floor_plan_versions) {
            return null;
        }

        let versionID = null;

        storeData.floor_plan_versions.forEach(version => {
            if(version.number === number) {
                versionID = version.id;

                return;
            }
        })
        
        return versionID;
    }
    
    const saveFloorPlan = (type, versionNumber) => {
        const postData = {
            data: storeData.floor_plan_elements,
            floorProperties: {
                width: storeData.floor_plan.width / 100,
                depth: storeData.floor_plan.depth / 100,
                positionX: storeData.texture.positionX,
                positionY: storeData.texture.positionY
            },
            rulers: storeData.rulers,
            id: storeData.floor_plan.floor_plan_id,
            type: type,
            floorPlanVersionID: getFloorPlanVersionId(versionNumber)
        };

        apiClient.post('/api/easy-floor/save-floor-plan-elements', postData).then(response => {
            if(response.data.success) {
                store.dispatch(setSelectedStoreElement(null));
                if(response.data.force_refresh) {
                    response.data.floor_plan.width = response.data.floor_plan.width * response.data.floor_plan.scale;
                    response.data.floor_plan.depth = response.data.floor_plan.depth * response.data.floor_plan.scale;
                    response.data.texture.width = response.data.texture.width * response.data.floor_plan.scale / 10;
                    response.data.texture.depth = response.data.texture.height * response.data.floor_plan.scale / 10;
                    
                    store.dispatch(setStoreData(response.data));
                    store.dispatch(setStoreUpdated(true));
                    store.dispatch(setIsSceneUpdated(true));
                    store.dispatch(setUnsavedModifications(false));

                    store.dispatch(setIsDragEnabled(true));
                    store.dispatch(setIsInfoWindowVisible(false));
                } else {    
                    store.dispatch(setFloorPlanElements(response?.data?.floor_plan_elements));
                    store.dispatch(setUnsavedModifications(false));
                    
                    store.dispatch(toggleMessageOpen('Store map updated', 'success'));
                }

                handleCloseSaveFloorPlanDialog();
            } else {
                store.dispatch(toggleMessageOpen('An error occurred while updating store map!', 'error'));
            }
        })
    }

    const handleRefreshStore = () => {
        const title = unsavedModifications ? 'You will loose all changes you made, continue?' : 'Are you sure?';

        store.dispatch(createConfirmAction(refreshStore, t(title)))
    }

    const refreshStore = () => {
        //store.dispatch(clearHistory());

        apiClient.post('/api/easy-floor/get', {id: storeData.floor_plan.floor_plan_id}).then(response => {
            response.data.floor_plan.width = response.data.floor_plan.width * response.data.floor_plan.scale;
            response.data.floor_plan.depth = response.data.floor_plan.depth * response.data.floor_plan.scale;
            response.data.texture.width = response.data.texture.width * response.data.floor_plan.scale / 10;
            response.data.texture.depth = response.data.texture.height * response.data.floor_plan.scale / 10;
            
            store.dispatch(setStoreData(response.data));
            store.dispatch(setUnsavedModifications(false));
            store.dispatch(setIsSceneUpdated(true));

            store.dispatch(setIsInfoWindowVisible(false));
            store.dispatch(setIsDragEnabled(true));

            // store.dispatch(setHistoryStep(1));
            // store.dispatch(setHistory(btoa(JSON.stringify(response.data.floor_plan_elements))));
        })
    }

    const handleSaveFloorPlan = () => {
        if(storeData.floor_plan.multiple_versions_enabled) {
            setIsSaveFloorPlanDialogOpen(true);
        } else {
            saveFloorPlan('current_version');
        }
    }
    const handleCloseSaveFloorPlanDialog = () => {
        setIsSaveFloorPlanDialogOpen(false);
    }

    const handleGridStateChange = () => {
        store.dispatch(setIsGridEnabled(!isGridEnabled));
        // store.dispatch(setIsSceneUpdated(true));
    }

    const handleChangeViewType = () => {
        if(is3D) {
            navigate('/app/easy-floor/map/' + floorPlanId);
        } else {
            navigate('/app/easy-floor/map/' + floorPlanId + '/preview');
        }
    }

    const handleEditFloorPlan = () => {
        navigate('/app/easy-floor/map/' + floorPlanId);

        store.dispatch(setIsFloorEdit(!isStoreEdit));
    }
    
    const handleDragStateChange = () => {
        store.dispatch(setIsDragEnabled(!isDragEnabled));
        // store.dispatch(setIsSceneUpdated(true));
    }

    const reCenterMap = () => {
        store.dispatch(setRecenterMap(true));
    }

    return ! is3D ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <StyledIconButton
                color="primary"
                title={t('Change View')}
                isActive={false}
                onClick={handleChangeViewType}
            >
                {!is3D ? <CubeIcon /> : <CubesIcon />}
            </StyledIconButton>
            <StyledIconButton
                color="primary"
                isActive={false}
                title={t('Edit Floor Plan')}
                onClick={handleEditFloorPlan}
            >
                <EditPencilIcon />
            </StyledIconButton>
            <StyledIconButton
                color="primary"
                isActive={isDragEnabled}
                title={t('Enable Drag')}
                onClick={handleDragStateChange}
            >
                <MoveIcon />
            </StyledIconButton>
            <StyledIconButton
                color="primary"
                isActive={isGridEnabled}
                title={t('Toggle Grid')}
                onClick={handleGridStateChange}
            >
                <ToggleGridIcon isGridOn={isGridEnabled} />
            </StyledIconButton>
            <StyledIconButton
                title={t('Recenter Map')}
                onClick={reCenterMap}
            >
                <CenterFocus />
            </StyledIconButton>
            <StyledIconButton
                title={t('Undo All Changes')}
                onClick={handleRefreshStore}
            >
                <Refresh />
            </StyledIconButton>
            <StyledIconButton
                title={t('Save Map')}
                onClick={handleSaveFloorPlan}
            >
                <Save />
            </StyledIconButton>
            <StyledIconButton
                color="primary"
                title={t('Show report')}
                isActive={open}
                onClick={() => {
                    store.dispatch(setSideBarIsOpened(open ? false : true));
                }}
            >
                <Article />
            </StyledIconButton>
            <SaveFloorPlanDialog
                open={isSaveFloorPlanDialogOpen}
                onSave={saveFloorPlan}
                handleClose={handleCloseSaveFloorPlanDialog}
                t={t}
                storeData={storeData}
            />
        </div>
    ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <StyledIconButton
                color="primary"
                title={t('Change View')}
                isActive={false}
                onClick={handleChangeViewType}
            >
                {!is3D ? <CubeIcon /> : <CubesIcon />}
            </StyledIconButton>
            <StyledIconButton
                title={t('Undo All Changes')}
                onClick={handleRefreshStore}
            >
                <Refresh />
            </StyledIconButton>
            <StyledIconButton
                title={t('Save Map')}
                onClick={handleSaveFloorPlan}
            >
                <Save />
            </StyledIconButton>
            <StyledIconButton
                color="primary"
                title={t('Show report')}
                isActive={open}
                onClick={() => {
                    store.dispatch(setSideBarIsOpened(open ? false : true));
                }}
            >
                <Article />
            </StyledIconButton>
            <SaveFloorPlanDialog
                open={isSaveFloorPlanDialogOpen}
                onSave={saveFloorPlan}
                handleClose={handleCloseSaveFloorPlanDialog}
                t={t}
                storeData={storeData}
            />
        </div> 
    )
}

const checkIfNextVersionNumberExists = (storeData) => {
    if(!storeData) {
        return;
    }

    let exists = false;
    const number = Number(storeData?.floor_plan?.number);

    storeData?.floor_plan_versions?.forEach(version => {
        if(version.number === number + 1) {
            exists = true;

            return;
        }
    });

    return exists;
}

const SaveFloorPlanDialog = ({open, handleClose, onSave, t, storeData}) => {
    const doesNextVersionNumberExists = checkIfNextVersionNumberExists(storeData);

    const Buttons = ({onSave}) => {
        return (
            <Grid container spacing={1} justifyContent='end'>
                <Grid item>
                    <Button variant="contained" onClick={() => onSave('current_version')}>
                        <Trans>Save to current version</Trans>
                    </Button>
                </Grid>
                {
                    doesNextVersionNumberExists
                        ?   [
                                <Grid item>
                                    <Button variant="contained" onClick={() => onSave('new_version', Number(storeData.floor_plan.number + 1))}>
                                        <Trans>Overwrite next version</Trans> {'(v'+ Number(storeData.floor_plan.number + 1) +')'}
                                    </Button>    
                                </Grid>,
                                <Grid item>
                                    <Button variant="contained" onClick={() => onSave('new_version', Number(storeData.floor_plan_versions.length + 1))}>
                                        <Trans>Create new version</Trans> {'(v'+ Number(storeData.floor_plan_versions.length + 1) +')'}
                                    </Button>    
                                </Grid>
                            ]
                        :   <Grid item>
                                <Button variant="contained" onClick={() => onSave('new_version')}>
                                    <Trans>Create version</Trans>
                                </Button>
                            </Grid>
                }
            </Grid>
        )
    }

    return (
        <CustomDialog
            fullWidth
            maxWidth="lg"
            scroll="paper"
            open={open}
            onClose={handleClose}
            title={t('Attention!')}
            buttons={<Buttons onSave={onSave} />}
        >
            Save Floor plan to current version or create new version
        </CustomDialog>
    )
}